import { useContext } from 'react';
import { TenantContext } from '../../../context/tenant-context';
import {
  CreateLineItemRequest,
  CustomerBalanceData,
  Invoice,
  InvoiceSearchSummaryData,
  LineItem,
  SearchInvoiceRequest,
  StatementPaymentSummary,
} from './invoice';
import { handlePaginatedRequest, handleRequest } from '../quipli-service-client';
import { InvoiceParsers } from './invoice-parsers';
import { Pagination } from '../pagination/pagination';
import { Payment, SearchPaymentRequest } from '../payment/payment';
import { PaymentParsers } from '../payment/payment-parsers';

export const useInvoiceApiV1 = () => {
  const { tenant, contextPath } = useContext(TenantContext);

  async function getInvoiceById(invoiceId: string): Promise<Invoice> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/invoices/${invoiceId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const invoice = await handleRequest<InvoiceParsers.InvoiceJson>(config);
    return InvoiceParsers.toInvoice(invoice);
  }

  async function searchInvoices(req: SearchInvoiceRequest): Promise<Pagination<Invoice>> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/invoices/search`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
    const { items, pagination } = await handlePaginatedRequest<InvoiceParsers.InvoiceJson>(config);
    return {
      items: items.map(InvoiceParsers.toInvoice),
      pagination,
    };
  }

  async function exportInvoices(req: SearchInvoiceRequest): Promise<BlobPart> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/invoices/export`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };

    return await handleRequest(
      {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      },
      true,
    );
  }

  async function searchSummaryData(req: SearchInvoiceRequest): Promise<InvoiceSearchSummaryData> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/invoices/searchSummaryData`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
    return handleRequest<InvoiceSearchSummaryData>(config);
  }

  async function createLineItem(invoiceId: string, req: CreateLineItemRequest): Promise<LineItem> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/invoices/${invoiceId}/lineItems`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
    const lineItem = await handleRequest<InvoiceParsers.LineItemJson>(config);
    return InvoiceParsers.toLineItem(lineItem);
  }

  async function deletePayment(invoiceId: string, paymentId: string): Promise<void> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/v1/invoices/${invoiceId}/payments/${paymentId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return handleRequest(config);
  }

  async function deleteLineItem(invoiceId: string, lineItemId: string): Promise<void> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/v1/invoices/${invoiceId}/lineItems/${lineItemId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return handleRequest(config);
  }

  async function updateLineItem(invoiceId: string, lineItemId: string, req: CreateLineItemRequest): Promise<LineItem> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/invoices/${invoiceId}/lineItems/${lineItemId}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
    const lineItem = await handleRequest<InvoiceParsers.LineItemJson>(config);
    return InvoiceParsers.toLineItem(lineItem);
  }

  async function getCustomerBalances(customerIds: number[]): Promise<CustomerBalanceData[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/invoices/getCustomerBalances`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        customerIds,
      },
    };
    const balances = await handleRequest<InvoiceParsers.CustomerBalanceDataJson>(config);
    return balances.items.map((b) => {
      return InvoiceParsers.toCustomerBalance(b);
    });
  }

  async function voidInvoice(invoiceId: string): Promise<Invoice> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/invoices/${invoiceId}/void`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const invoice = await handleRequest<InvoiceParsers.InvoiceJson>(config);
    return InvoiceParsers.toInvoice(invoice);
  }

  async function searchPayments(req: SearchPaymentRequest): Promise<Pagination<Payment>> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/invoices/payments`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
    const { items, pagination } = await handlePaginatedRequest<PaymentParsers.PaymentJson>(config);
    return {
      items: items.map(PaymentParsers.toPayment),
      pagination,
    };
  }

  async function getStatementPaymentSummary(req: {
    amount: number;
    userId: number;
    selectedInvoices: string[];
  }): Promise<StatementPaymentSummary[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/invoices/statementPaymentSummary`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
    const sp = await handleRequest<InvoiceParsers.StatementPaymentSummaryJson[]>(config);
    return sp.map(InvoiceParsers.toStatementPaymentSummary);
  }

  async function getApplicablePaymentSummary(req: {
    userId: number;
  }): Promise<{ unapplied: Payment[]; credits: Payment[] }> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/invoices/applicablePayments/${req.userId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const p = await handleRequest<{ credits: PaymentParsers.PaymentJson[]; unapplied: PaymentParsers.PaymentJson[] }>(
      config,
    );
    return { unapplied: p.unapplied.map(PaymentParsers.toPayment), credits: p.credits.map(PaymentParsers.toPayment) };
  }

  return {
    getInvoiceById,
    searchInvoices,
    searchSummaryData,
    exportInvoices,
    createLineItem,
    deletePayment,
    deleteLineItem,
    updateLineItem,
    getCustomerBalances,
    voidInvoice,
    searchPayments,
    getStatementPaymentSummary,
    getApplicablePaymentSummary,
  };
};
